import React, { useEffect, useState } from 'react'
import { Calendar, CalendarStep } from '../types/dimmingProfile.types'
import { ActionWrapper, PassThroughAction } from '../types/presenter.types'
import {
  dimmingProfileStrings,
  getLocale,
  Locale,
} from '../i18n/dimmingProfileI18n'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'
import ConfirmationModal from '../../../common/components/atom/ConfirmationModal/ConfirmationModal'
import CalendarCurve from './CalendarCurve'
import CalendarStepsCurve from './CalendarStepsCurve'
import DailyStepsCurve from './DailyStepsCurve'
import AnnualZenithCurve from './AnnualZenithCurve'

// Coordinates resolved from the fleet's first linked minigrid. When present and
// the calendar has no coordinates yet, the editor prefills latitude/longitude.
export interface LinkedMinigridCoords {
  lat: number
  lng: number
  minigridName: string
}

interface CalendarEditorProps {
  // Controlled value of the 0x20 zenith calendar (sunrise/sunset + position +
  // flags). The fleet feature owns the state and persists it with the profile.
  value: Calendar
  onChange: (next: Calendar) => void
  // Optional locale override (defaults to the module-level current locale).
  locale?: Locale
  // Coordinates from the fleet's first linked minigrid (if any). Used to prefill
  // latitude/longitude and to drive the hint shown under the inputs.
  linkedMinigrid?: LinkedMinigridCoords | null
  // Lecture seule : champs, cases, boutons et courbes figés. Seul le recalage
  // des coordonnées sur la minigrid liée continue d'émettre (valeur dérivée).
  readOnly?: boolean
  // Enveloppe des boutons d'action (ex. infobulle RBAC en lecture seule).
  actionWrapper?: ActionWrapper
}

const cellInput: React.CSSProperties = {
  width: 80,
  height: 26,
  padding: '2px 6px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: 12,
}

// Read-only look for the lat/long fields — coordinates are derived from the
// linked minigrid, so they are displayed greyed and non-editable.
const readOnlyInput: React.CSSProperties = {
  background: '#f3f4f6',
  color: '#6b7280',
  borderColor: '#e5e7eb',
  cursor: 'not-allowed',
}

type StepsKey = 'sunrise_steps' | 'sunset_steps'

// Example zenith configuration (mirrors the momi's env defaults
// DATA_SUNRISE_STEPS / DATA_SUNSET_STEPS): 30% during dawn twilight then off
// for the day; at dusk 30% → 50% as night falls, settling at 20% deep night.
// Sorted ascending by zenith, the editor's canonical order.
const EXAMPLE_SUNRISE_STEPS: CalendarStep[] = [
  { zenith_angle_deg: 90, dimming_level_percent: 0 },
  { zenith_angle_deg: 95, dimming_level_percent: 30 },
  { zenith_angle_deg: 100, dimming_level_percent: 30 },
]
const EXAMPLE_SUNSET_STEPS: CalendarStep[] = [
  { zenith_angle_deg: 90, dimming_level_percent: 0 },
  { zenith_angle_deg: 95, dimming_level_percent: 30 },
  { zenith_angle_deg: 100, dimming_level_percent: 50 },
  { zenith_angle_deg: 105, dimming_level_percent: 20 },
]

// Form/table editor for the 0x20 zenith calendar (v1: no graphical curve). Two
// tables (sunrise/sunset), latitude/longitude inputs, and the four boolean flags.
export const CalendarEditor: React.FC<CalendarEditorProps> = ({
  value,
  onChange,
  locale,
  linkedMinigrid,
  readOnly = false,
  actionWrapper: ActionSlot = PassThroughAction,
}) => {
  const strings = dimmingProfileStrings(locale ?? getLocale())
  // Émission des éditions de l'utilisateur : neutralisée en lecture seule.
  const edit = (next: Calendar) => {
    if (!readOnly) onChange(next)
  }

  // Sync lat/long from the linked minigrid. The fields are READ-ONLY (derived
  // from the minigrid record), so they must FOLLOW it — including correcting a
  // calendar that captured the minigrid's coordinates BEFORE they were fixed
  // (the previous prefill-only-when-0 logic froze wrong coords forever, with
  // no way to edit them). Runs whenever the minigrid or current coords change;
  // the corrected values reach the lamps on the next save (auto-push).
  useEffect(() => {
    if (!linkedMinigrid) return
    const next: Partial<Calendar> = {}
    // On NE filtre PLUS sur `!== 0` : 0 est une coordonnée légitime (équateur /
    // méridien de Greenwich). Les champs suivent la minigrid dès qu'ils en
    // diffèrent, y compris pour se caler sur une valeur 0 renseignée.
    if (value.latitude_deg !== linkedMinigrid.lat) {
      next.latitude_deg = linkedMinigrid.lat
    }
    if (value.longitude_deg !== linkedMinigrid.lng) {
      next.longitude_deg = linkedMinigrid.lng
    }
    if (Object.keys(next).length > 0) {
      onChange({ ...value, ...next })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedMinigrid, value.latitude_deg, value.longitude_deg])

  const setSteps = (key: StepsKey, steps: CalendarStep[]) =>
    edit({ ...value, [key]: steps })

  const addStep = (key: StepsKey) =>
    setSteps(key, [
      ...value[key],
      { zenith_angle_deg: 90, dimming_level_percent: 0 },
    ])

  const removeStep = (key: StepsKey, index: number) =>
    setSteps(
      key,
      value[key].filter((_, i) => i !== index),
    )

  const setStepField = (
    key: StepsKey,
    index: number,
    field: keyof CalendarStep,
    raw: number,
  ) => {
    // Vider un champ numérique produit parseFloat('')/parseInt('') === NaN.
    // Écrit tel quel, NaN casse le tri (levelForZenith/sortByZenith), génère un
    // attribut SVG `d` invalide, et JSON.stringify(NaN) === "null" → config de
    // lampe corrompue côté backend. On ignore toute valeur non finie : le champ
    // conserve sa valeur précédente (le clamp de plage se fait au save/édition
    // graphique). Voir revue MR !2.
    if (!Number.isFinite(raw)) return
    setSteps(
      key,
      value[key].map((s, i) => (i === index ? { ...s, [field]: raw } : s)),
    )
  }

  const setFlag = (
    field:
      | 'prefers_meta_pos'
      | 'clamps_profiles'
      | 'clamps_dig'
      | 'ignore_gnss',
    checked: boolean,
  ) => edit({ ...value, [field]: checked })

  const renderTable = (title: string, key: StepsKey) => {
    const steps = value[key]
    return (
      <div style={{ flex: '1 1 280px', minWidth: 260 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          {title}
        </div>
        {steps.length === 0 ? (
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>
            {strings.noCalendarSteps}
          </p>
        ) : (
          <table
            style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}
          >
            <thead>
              <tr style={{ textAlign: 'left', color: '#374151' }}>
                <th style={{ padding: '4px 8px' }}>{strings.zenithAngle}</th>
                <th style={{ padding: '4px 8px' }}>{strings.dimmingLevel}</th>
                <th style={{ padding: '4px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {steps.map((s, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px 8px' }}>
                    <input
                      type="number"
                      step={1}
                      disabled={readOnly}
                      value={s.zenith_angle_deg}
                      onChange={(e) =>
                        setStepField(
                          key,
                          i,
                          'zenith_angle_deg',
                          parseFloat(e.target.value),
                        )
                      }
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      disabled={readOnly}
                      value={s.dimming_level_percent}
                      onChange={(e) =>
                        setStepField(
                          key,
                          i,
                          'dimming_level_percent',
                          parseInt(e.target.value, 10),
                        )
                      }
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <ActionSlot>
                      <ButtonV2
                        type="button"
                        onClick={() => removeStep(key, i)}
                        disabled={readOnly}
                        variant="outlined"
                        color="secondary"
                        size="small"
                      >
                        {strings.removeCalendarStep}
                      </ButtonV2>
                    </ActionSlot>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 6 }}>
          <ActionSlot>
            <ButtonV2
              type="button"
              onClick={() => addStep(key)}
              disabled={readOnly}
              variant="outlined"
              color="primary"
              size="small"
            >
              {strings.addCalendarStep}
            </ButtonV2>
          </ActionSlot>
        </div>
      </div>
    )
  }

  // Confirmation for the example fill, via the in-app ConfirmationModal:
  // window.confirm is silently BLOCKED in some embeddings (sandboxed iframe /
  // browser setting) and then returns false, making the button look dead when
  // steps already exist — never rely on native dialogs here.
  const [confirmingExample, setConfirmingExample] = useState(false)

  // Fill the editor with the example configuration. Coordinates and flags are
  // left untouched (coords follow the minigrid).
  const doApplyExample = () => {
    setConfirmingExample(false)
    edit({
      ...value,
      sunrise_steps: EXAMPLE_SUNRISE_STEPS.map((s) => ({ ...s })),
      sunset_steps: EXAMPLE_SUNSET_STEPS.map((s) => ({ ...s })),
    })
  }

  const handleExampleClick = () => {
    if (readOnly) return
    const hasSteps =
      value.sunrise_steps.length > 0 || value.sunset_steps.length > 0
    if (hasSteps) {
      setConfirmingExample(true) // ask first — replacing is destructive
    } else {
      doApplyExample()
    }
  }

  const flag = (
    field:
      | 'prefers_meta_pos'
      | 'clamps_profiles'
      | 'clamps_dig'
      | 'ignore_gnss',
    label: string,
    hint?: string,
  ) => (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: '#374151',
      }}
    >
      <input
        type="checkbox"
        disabled={readOnly}
        checked={value[field]}
        onChange={(e) => setFlag(field, e.target.checked)}
      />
      <span>
        {label}
        {hint && (
          <span style={{ color: '#6b7280', fontSize: 12 }}> — {hint}</span>
        )}
      </span>
    </label>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h4 style={{ margin: 0, fontSize: 15, color: '#111827' }}>
          {strings.calendarTitle}
        </h4>
        <ActionSlot>
          <ButtonV2
            type="button"
            onClick={handleExampleClick}
            disabled={readOnly}
            variant="outlined"
            color="primary"
            size="small"
          >
            {strings.calendarExample}
          </ButtonV2>
        </ActionSlot>
      </div>

      <ConfirmationModal
        isOpen={confirmingExample}
        title={strings.calendarExampleConfirmTitle}
        message={strings.calendarExampleConfirm}
        confirmText={strings.calendarExampleApply}
        cancelText={strings.calendarExampleCancel}
        onConfirm={doApplyExample}
        onCancel={() => setConfirmingExample(false)}
        type="warning"
      />
      <div style={{ fontSize: 12, color: '#6b7280' }}>{strings.zenithHint}</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {renderTable(strings.sunrise, 'sunrise_steps')}
        {renderTable(strings.sunset, 'sunset_steps')}
      </div>

      {/* Direct zenith-axis step editors (mirror the profile graphical editor). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <CalendarStepsCurve
          title={strings.calendarSunriseEditor}
          color="#f59e0b"
          phase="sunrise"
          steps={value.sunrise_steps}
          strings={strings}
          readOnly={readOnly}
          onChange={(next) => edit({ ...value, sunrise_steps: next })}
        />
        <CalendarStepsCurve
          title={strings.calendarSunsetEditor}
          color="#2563eb"
          phase="sunset"
          steps={value.sunset_steps}
          strings={strings}
          readOnly={readOnly}
          onChange={(next) => edit({ ...value, sunset_steps: next })}
        />
      </div>

      {/* Read-only intensity-vs-time preview (no onChange => editing disabled). */}
      <CalendarCurve calendar={value} strings={strings} />

      <DailyStepsCurve calendar={value} strings={strings} />

      <AnnualZenithCurve calendar={value} strings={strings} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <label style={{ fontSize: 13, color: '#374151' }}>
          <div style={{ marginBottom: 4 }}>{strings.latitude}</div>
          <input
            type="number"
            step={0.0001}
            value={value.latitude_deg}
            readOnly
            aria-readonly
            title={strings.coordsReadOnlyHint}
            style={{ ...cellInput, ...readOnlyInput, width: 120 }}
          />
        </label>
        <label style={{ fontSize: 13, color: '#374151' }}>
          <div style={{ marginBottom: 4 }}>{strings.longitude}</div>
          <input
            type="number"
            step={0.0001}
            value={value.longitude_deg}
            readOnly
            aria-readonly
            title={strings.coordsReadOnlyHint}
            style={{ ...cellInput, ...readOnlyInput, width: 120 }}
          />
        </label>
      </div>

      <div style={{ fontSize: 12, color: '#6b7280' }}>
        {linkedMinigrid
          ? strings.coordsFromMinigrid(linkedMinigrid.minigridName)
          : strings.coordsLinkMinigridHint}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {flag('clamps_profiles', strings.clampsProfiles, strings.clampsProfilesHint)}
        {flag('prefers_meta_pos', strings.prefersMetaPos)}
        {flag('clamps_dig', strings.clampsDig)}
        {flag('ignore_gnss', strings.ignoreGnss)}
      </div>
    </div>
  )
}

export default CalendarEditor
